
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React from "react";
import useTranslation from "next-translate/useTranslation";
import Trans from "next-translate/Trans";

import { Head } from "@/components/Head";
import { useTheme } from "@/hooks/useTheme";
import { Converter } from "@/containers/Converter";

import { MagicianSection } from "@/sections/MagicianSection";
import { TrustedBySection } from "@/sections/TrustedBySection";
import { SelectFormatSection } from "@/sections/SelectFormatSection";
import { ApiTeaserSection } from "@/sections/ApiTeaserSection";
import { ConversionSection } from "@/sections/ConversionSection";
import { ShareDesignSection } from "@/sections/ShareDesignSection";
import { PricingSection } from "@/sections/PricingSection";
import { ConverterFaqSection } from "@/sections/ConverterFaqSection";

const GoogleSlidesToCanva = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  return (
    <>
      <Head
        pageNamespace="page/GoogleSlidesToCanva"
        ogImage="/images/googleslides2canva-og-image.jpg"
      />

      <MagicianSection
        title={t("page/GoogleSlidesToCanva:title")}
        subtitle={t("page/GoogleSlidesToCanva:subtitle")}
        magicianLogoFile="canva"
      >
        <Converter disabled />
      </MagicianSection>

      <TrustedBySection />

      <ApiTeaserSection
        title={
          ((
            <Trans
              i18nKey="page/GoogleSlidesToCanva:section/apiTeaser.title"
              components={[<span style={{ color: colors.ORANGE }} key={0} />]}
            />
          ) as unknown) as string
        }
        image={{
          src: "/images/googleslides-to-canva-example.png",
          alt: "Google Slides to Canva converter example",
        }}
        backgroundColor={colors.LIGHT_GRAY1}
        cta={{
          title: t("common:actions.signUp"),
        }}
        elements={t(
          "page/GoogleSlidesToCanva:section/apiTeaser.apiTeaserElements",
          null,
          {
            returnObjects: true,
          },
        )}
      />

      <ShareDesignSection
        id="ShareDesignSection"
        title={
          ((
            <Trans
              i18nKey="page/GoogleSlidesToCanva:section/shareDesign.title"
              components={[<span key="0" style={{ color: colors.ORANGE }} />]}
            />
          ) as unknown) as string
        }
        image={{
          src: "/images/google-slides-to-canva-converted-file-elements.png",
          alt: t("page/GoogleSlidesToCanva:section/shareDesign.imageAlt"),
        }}
        backgroundColor={colors.LIGHT_GRAY1}
        cta={{
          title: t("page/GoogleSlidesToCanva:section/shareDesign.ctaButton"),
        }}
        elements={[
          {
            id: "ELEMENT#1",
            iconType: "SPREAD-ICON",
            title: t(
              "page/GoogleSlidesToCanva:section/shareDesign.elements.pathElementsTitle",
            ),
            text: t(
              "page/GoogleSlidesToCanva:section/shareDesign.elements.pathElementsText",
            ),
          },
          {
            id: "ELEMENT#2",
            iconType: "FOLDER-SIMPLE-ICON",
            title: t(
              "page/GoogleSlidesToCanva:section/shareDesign.elements.folderStructureTitle",
            ),
            text: t(
              "page/GoogleSlidesToCanva:section/shareDesign.elements.folderStructureText",
            ),
          },
          {
            id: "ELEMENT#3",
            iconType: "CHECK-ICON-EMPTY",
            title: t(
              "page/GoogleSlidesToCanva:section/shareDesign.elements.folderAccurateTitle",
            ),
            text: t(
              "page/GoogleSlidesToCanva:section/shareDesign.elements.folderAccurateText",
            ),
          },
        ]}
      />

      <PricingSection />

      <ConversionSection
        title={t("section/conversion:title")}
        iconBadges={[
          {
            iconType: "SHEETS",
            text: t("section/conversion:badges.supportsGroupLayers"),
          },
          {
            iconType: "SYNC-ICON",
            text: t("section/conversion:badges.convertsComponentsSymbols"),
          },
          {
            iconType: "TV",
            text: t("section/conversion:badges.artboardStyleSupport"),
          },
        ]}
        elements={{
          from: {
            iconType: "GOOGLE-SLIDES",
            image: "/images/google-slides-preview-2.png",
            title: "Google Slides Demo Preview",
          },
          to: {
            iconType: "CANVA",
            image: "/images/canva-preview-slides.png",
            title: "Canva Demo Preview",
          },
        }}
      />

      <ConverterFaqSection
        title={t("page/GoogleSlidesToCanva:section/faq.title")}
        items={[
          {
            question: t(
              "page/GoogleSlidesToCanva:section/faq.howToImport.question",
            ),
            answer: (
              <Trans
                i18nKey="page/GoogleSlidesToCanva:section/faq.howToImport.answer"
                components={[
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    href="/blog/how-to-convert-powerpoint-to-canva-in-under-2-minutes"
                    key="0"
                  />,
                ]}
              />
            ),
            answerText: t(
              "page/GoogleSlidesToCanva:section/faq.howToImport.answer",
            ),
            icon: "SWITCH-ICON",
          },
          {
            question: t("page/GoogleSlidesToCanva:section/faq.free.question"),
            answer: t("page/GoogleSlidesToCanva:section/faq.free.answer"),
            answerText: t("page/GoogleSlidesToCanva:section/faq.free.answer"),
            icon: "FREE-ICON",
          },
          {
            question: t(
              "page/GoogleSlidesToCanva:section/faq.editable.question",
            ),
            answer: t("page/GoogleSlidesToCanva:section/faq.editable.answer"),
            answerText: t(
              "page/GoogleSlidesToCanva:section/faq.editable.answer",
            ),
            icon: "PEN-ICON",
          },
          {
            question: t(
              "page/GoogleSlidesToCanva:section/faq.reverse.question",
            ),
            answer: (
              <Trans
                i18nKey="page/GoogleSlidesToCanva:section/faq.reverse.answer"
                components={[
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a href="/converter/canva-to-googleslides" key="0" />,
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    href="/blog/how-to-transfer-canva-to-google-slides"
                    key="1"
                  />,
                ]}
              />
            ),
            answerText: t(
              "page/GoogleSlidesToCanva:section/faq.reverse.answer",
            ),
            icon: "ARROW-POINTER-ICON",
          },
        ]}
      />

      <SelectFormatSection title={t("section/selectFormat:title")} />
    </>
  );
};

// Temporal workaround for https://github.com/vercel/next.js/discussions/18396
 async function _getStaticProps() {
  return {
    props: {},
  };
}

export default GoogleSlidesToCanva;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/converter/google-slides-to-canva',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  